import { useSyncExternalStore } from 'react';
import { useAppSelector } from '../redux/hooks';
import { COURSE_VERSIONING_PILOT_ORG_IDS } from '../utils/constants';
import {
  isCourseVersioningDisabledForSession,
  subscribeCourseVersioningSession
} from '../utils/courseVersioningSession';

/**
 * Reads the org-level `courseVersioning` feature flag
 * (BE: organization.flags.courseVersioning).
 *
 * Gates course versioning (docs/API-course-versioning.md):
 *  - the Designer edits a draft (autosaved) instead of the live template,
 *    with Version History and Publish update replacing Save Changes
 *  - the program edit lock while the Designer is open
 *  - learner What's new pop-up, Updated / Action needed lesson badges and
 *    the "program updated" bell entry
 *
 * Defaults to false (legacy Designer) when the flag is absent. Orgs in the FE
 * pilot list are force-enabled until the BE flag rollout completes. A
 * `VERSIONING_OFF` answer from any versioning endpoint switches the feature
 * off for the rest of the session (see utils/courseVersioningSession).
 */
const useCourseVersioning = (): boolean => {
  const disabledForSession = useSyncExternalStore(
    subscribeCourseVersioningSession,
    isCourseVersioningDisabledForSession,
    isCourseVersioningDisabledForSession
  );
  const flagOn = useAppSelector((state) => {
    const org = state.orgDetails.data as { authId?: string; flags?: { courseVersioning?: boolean } } | undefined;
    if (org?.flags?.courseVersioning === true) return true;
    return Boolean(org?.authId && COURSE_VERSIONING_PILOT_ORG_IDS.includes(org.authId));
  });
  return flagOn && !disabledForSession;
};

export default useCourseVersioning;
